import replace from 'lodash/replace';
import reduce from 'lodash/reduce';
import uniqBy from 'lodash/uniqBy';

/**
 * The data shape returned by the server doesn't match the client 100%, so do some simple transformation.
 * @param data - The incident object stored in the database
 * @returns object - transformed data object that fits our ag-grid column definitions
 */

export function transformIncident(data) {
  return {
    ...data,
    // This is the value set by the user and visible to the user. Not the UUID.
    incidentId: data.incidentIdentifier,
    selected: false,
    severity: data.severity.name
      ? replace(data.severity.name.toUpperCase(), ' ', '_')
      : data.severity,
    status: data.status.name
      ? replace(data.status.name.toUpperCase(), ' ', '_')
      : data.status,
  };
}

/**
 * Normally once we fetch a list of data from the server, we can just do some simple shape transformation and dump it
 * into the store, but not this time. This time we want to preserve the "selected" state from before the fetch, so we'll have to:
 * 1. Figure out if the new incident was among the previously selected incidents.
 * 2. If so, it should still be selected this time.
 * 3. if not, it didn't appear previously so we safely treat it as unselected.
 * @param {[number]} oldSelectedIncidentIDs - The list of incident IDs currently on the table that's also selected
 * @param {[]} newIncidents - The list of incidents fetched from server.
 * @returns {[]} The parsed incident list, with `selected` calculated.
 */
export function transformIncidents(oldSelectedIncidentIDs, newIncidents) {
  return newIncidents.map(newIncident => ({
    ...newIncident,
    // This is the value set by the user and visible to the user. Not the UUID.
    incidentId: newIncident.incidentIdentifier,
    severity: newIncident.severity.name
      ? replace(newIncident.severity.name.toUpperCase(), ' ', '_')
      : newIncident.severity,
    status: newIncident.status.name
      ? replace(newIncident.status.name.toUpperCase(), ' ', '_')
      : newIncident.status,
    selected: !!oldSelectedIncidentIDs.find(
      selectedIncidentID => selectedIncidentID === newIncident.id,
    ),
  }));
}

/**
 * If a user is paged 10 times, there will be 10 resolvers with their name.
 * Each resolver has its own unique resolver ID, but they all share the same user ID.
 * This function deduplicates resolvers by their resolver ID. Check this function's test
 * in ./utils.spec.js to see why it's needed to fix COR-49054
 * @param {object} payload
 */
export function getUniqueResolvers(payload) {
  const resolversInRoles = reduce(
    Object.values(payload).filter(r => r.resolverType === 'ROLE'),
    (resolvers, role) => [...resolvers, ...role.sortable.resolvers.data],
    [],
  );

  const resolversNotInRoles = Object.values(payload)
    .filter(r => r.resolverType === 'RESOLVER')
    .map(r => r.sortable);

  return uniqBy([...resolversInRoles, ...resolversNotInRoles], 'id');
}
